
import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { PhoneInput } from "@/components/PhoneInput";

export const CallToAction: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("1"); // Default to US/Canada
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [callId, setCallId] = useState<string | null>(null);
  const [callError, setCallError] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber) return;
    
    // Reset any previous error state
    setCallError(null);
    setIsSubmitting(true);
    
    try {
      // Format the full phone number with country code
      const fullPhoneNumber = `+${countryCode}${phoneNumber}`;
      
      console.log("Initiating call to:", fullPhoneNumber);
      
      // Call our edge function to log the call request
      const { data, error } = await supabase.functions.invoke('initiate-call', {
        body: {
          to_number: fullPhoneNumber,
          first_message: "Hello, this is HeyVail calling. I'd like to discuss how we can turn AI promises into real revenue for your business."
        }
      });
      
      console.log("Edge function response:", data, error);

      if (error) {
        console.error("Error initiating call:", error);
        setCallError("Failed to initiate call. Please try again or contact support.");
        toast({
          title: "Error",
          description: "There was a problem initiating your call. Please try again.",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      // Success path
      setCallId(data.call_id);
      setSubmitted(true);
      setPhoneNumber("");
      
      toast({
        title: "Call Requested",
        description: "We'll call you shortly!",
      });

      // Reset submitted state after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error("Error submitting call request:", error);
      setCallError("An unexpected error occurred. Please try again later.");
      toast({
        title: "Error",
        description: "There was a problem processing your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-background">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-4xl mx-auto fade-in">
          <div className="glass-card p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10 translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10 -translate-x-1/2 translate-y-1/2"></div>
            
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-light text-foreground mb-6">
                Ready to turn AI promises into revenue?
              </h2>
              <p className="text-xl text-muted-foreground">
                Stop wondering if AI can really grow your business. Let's build the complete revenue system that finds your perfect customers, qualifies them automatically, and hands your sales team prospects who are ready to buy.
              </p>
            </div>
            
            <div className="max-w-md mx-auto">
              {callError && (
                <Alert className="mb-6 bg-red-500 bg-opacity-10 border-red-500">
                  <AlertTitle>Call Error</AlertTitle>
                  <AlertDescription>{callError}</AlertDescription>
                </Alert>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-grow">
                    <label htmlFor="phone" className="sr-only">Phone Number</label>
                    <PhoneInput
                      value={phoneNumber}
                      onChange={setPhoneNumber}
                      countryCode={countryCode}
                      onCountryCodeChange={setCountryCode}
                      disabled={isSubmitting || submitted}
                      onSubmit={() => handleSubmit(new Event('submit') as unknown as React.FormEvent)}
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary-button whitespace-nowrap flex items-center justify-center gap-2"
                    disabled={isSubmitting || submitted}
                  >
                    {isSubmitting ? (
                      "Processing..."
                    ) : submitted ? (
                      "Thanks! We'll call soon"
                    ) : (
                      <>Call Me <ArrowRight className="h-4 w-4" /></>
                    )}
                  </button>
                </div>
              </form>
              
              <div className="mt-8 text-center">
                <p className="text-muted-foreground text-sm">
                  By submitting, you agree to our{" "}
                  <a href="/terms-of-service" className="text-primary hover:underline">Terms of Service</a>
                  {" "}and{" "}
                  <a href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
